import interpolateComponents from '@automattic/interpolate-components';
import type { JSX } from 'react';
import { sprintf } from 'sprintf-js';

import type { I18nFn } from './types/I18nFn';
import { I18nLocale } from './types/I18nLocale';

function unboundI18nFn(
  this: I18nLocale,
  text: string,
  ...dataArguments: Record<string, unknown>[]
) {
  const translatedText = (() => {
    if (typeof text !== 'object')
      throw new Error(`Missing i18n match for ${text}`);

    return text[this];
  })();

  if (dataArguments.length === 0) return translatedText;

  const [data] = dataArguments;

  const keys = Object.keys(data);
  if (keys.length === 0) return translatedText;

  const args: Record<string, string | number> = {};
  const components: Record<string, JSX.Element> = {};

  for (const key of keys) {
    const value = data[key];

    if (typeof value === 'string' || typeof value === 'number')
      args[key] = value;
    else components[key] = value as JSX.Element;
  }

  const translatedTextWithArgs = sprintf(translatedText, args);

  const hasComponents = Object.keys(components).length > 0;
  if (!hasComponents) return translatedTextWithArgs;

  return interpolateComponents({
    mixedString: translatedTextWithArgs,
    components,
  });
}

const fns = {
  en: unboundI18nFn.bind(I18nLocale.EN) as I18nFn,
  es: unboundI18nFn.bind(I18nLocale.ES) as I18nFn,
  pt: unboundI18nFn.bind(I18nLocale.PT) as I18nFn,
} satisfies Record<I18nLocale, I18nFn>;

export function getI18n(locale: I18nLocale): {
  locale: I18nLocale;
  i18n: I18nFn;
} {
  return { locale, i18n: fns[locale] };
}
